import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

/**
 * Custom hook for MKT Budget Application Calendar (TimeLine) auto-save functionality
 * 
 * This hook manages:
 * - Auto-save detection and restoration for TimeLine calendar
 * - Tracks timelineCards and pendingDeletes changes
 * - Polling for data changes every 500ms
 * 
 */
export const useMKTCostCalendarAutoSave = (
  isEditing: boolean,
  timelineCards: any[],
  pendingDeletes: any[],
  setTimelineCards: (data: any[]) => void,
  setPendingDeletes: (data: any[]) => void,
  timelineRef: React.RefObject<any>
) => {
  // Auto-save state
  const [calendarAutoSaveVisible, setCalendarAutoSaveVisible] = useState(false);
  const [calendarEditData, setCalendarEditData] = useState<any[]>([]);
  const [calendarValidationErrors, setCalendarValidationErrors] = useState<{ [key: string]: { project?: boolean; status?: boolean } }>({});
  const isManualUpdateRef = useRef(false);

  // Auto-save: construct unique pageKey for MKT Budget Application Calendar
  const calendarPageKey = useMemo(() => {
    return EDIT_PAGE_KEY_MAP.publishing_mktcost_mkt_budget_application_calendar;
  }, []);

  // Auto-save: get methods (check if auto-save exists)
  const calendarAutoSaveMethods: any = useAutoSaveParams({
    pageKey: calendarPageKey,
  });

  const {
    getSaveParamsValue: getCalendarSaveParamsValue,
    showRestorePrompt: showCalendarRestorePrompt,
    deleteSaveParamsByPageKey: deleteCalendarSaveParamsByPageKey
  } = calendarAutoSaveMethods;

  // Track edit data changes for Calendar auto-save - Poll frequently to capture real-time edits
  useEffect(() => {
    if (isEditing) {
      // Initialize edit data and initial params when entering edit mode with data
      if (timelineCards.length > 0) {
        // Capture initial params if not already captured
        if (calendarInitialParamsRef.current.timelineCards.length === 0) {
          calendarInitialParamsRef.current = {
            timelineCards: JSON.parse(JSON.stringify(timelineCards)),
            pendingDeletes: [],
            validationErrors: {},
          };
        }
        setCalendarEditData(timelineCards);
      }

      const interval = setInterval(() => {
        // Skip polling if we just did a manual update
        if (isManualUpdateRef.current) {
          isManualUpdateRef.current = false;
          return;
        }

        // Get editValues and validationErrors from TimeLine component
        const editValues = timelineRef.current?.getEditValues?.() || {};
        const validationErrors = timelineRef.current?.getValidationErrors?.() || {};
        
        // Merge editValues into timelineCards to get the current state
        const mergedCards = timelineCards.map((card: any) => {
          if (editValues[card.id]) {
            return {
              ...card,
              project: editValues[card.id].project || card.project,
              status: editValues[card.id].status || card.status,
              remark: editValues[card.id].remark || card.remark,
            };
          }
          return card;
        });

        // Track merged cards and validation errors changes
        setCalendarEditData((prev: any[]) => {
          const prevStr = JSON.stringify(prev);
          const currentStr = JSON.stringify(mergedCards);
          if (prevStr !== currentStr) {
            return mergedCards;
          }
          return prev;
        });

        // Track validation errors changes
        setCalendarValidationErrors((prev: any) => {
          const prevStr = JSON.stringify(prev);
          const currentStr = JSON.stringify(validationErrors);
          if (prevStr !== currentStr) {
            return validationErrors;
          }
          return prev;
        });
      }, 500); // Check every 500ms for changes

      return () => clearInterval(interval);
    } else if (!isEditing) {
      // Reset when exiting edit mode
      setCalendarEditData([]);
      setCalendarValidationErrors({});
      calendarInitialParamsRef.current = {
        timelineCards: [],
        pendingDeletes: [],
        validationErrors: {},
      };
    }
  }, [isEditing, timelineCards, timelineRef]);

  // Current save params for auto-save comparison
  const calendarCurrentSaveParams = useMemo(() => {
    if (!isEditing) {
      // When not editing, return empty params to prevent auto-save from triggering
      return {
        timelineCards: [],
        pendingDeletes: [],
        validationErrors: {},
      };
    }

    // When editing, use actual edit data
    return {
      timelineCards: calendarEditData.length > 0 ? calendarEditData : timelineCards,
      pendingDeletes: pendingDeletes,
      validationErrors: calendarValidationErrors,
    };
  }, [isEditing, calendarEditData, timelineCards, pendingDeletes, calendarValidationErrors]);

  // Initial params ref for auto-save
  const calendarInitialParamsRef = useRef({
    timelineCards: [],
    pendingDeletes: [],
    validationErrors: {},
  });


  // Start auto-saving Calendar when in edit mode
  useAutoSaveParams({
    pageKey: calendarPageKey,
    initialParams: calendarInitialParamsRef.current,
    saveParamsCur: calendarCurrentSaveParams,
    isBegin: isEditing,
    isChangeValue: true,
  });

  // Handler: Check for auto-save before entering edit mode
  const handleCalendarEdit = useCallback((originalEditHandler: () => void) => {
    if (showCalendarRestorePrompt(calendarPageKey)) {
      setCalendarAutoSaveVisible(true);
    } else {
      originalEditHandler();
    }
  }, [showCalendarRestorePrompt, calendarPageKey]);

  // Handler: Discard auto-save
  const handleCalendarAutoSaveCancel = useCallback((onCancel?: () => void) => {
    setCalendarAutoSaveVisible(false);
    deleteCalendarSaveParamsByPageKey(calendarPageKey);
    setPendingDeletes([]); // Clear pending deletes when discarding auto-save
    if (onCancel) {
      onCancel();
    }
  }, [deleteCalendarSaveParamsByPageKey, calendarPageKey, setPendingDeletes]);

  // Handler: Restore from auto-save
  const handleCalendarAutoSaveConfirm = useCallback((onConfirm?: () => void) => {
    const saveValue = getCalendarSaveParamsValue(calendarPageKey) || {};

    setCalendarAutoSaveVisible(false);

    // Restore pending deletes if they exist
    if (saveValue.pendingDeletes && Array.isArray(saveValue.pendingDeletes)) {
      setPendingDeletes(saveValue.pendingDeletes);
    }

    // Update timelineCards with saved data
    if (saveValue.timelineCards && Array.isArray(saveValue.timelineCards)) {
      setTimelineCards(saveValue.timelineCards);
      // Update edit data state for auto-save tracking
      setCalendarEditData(saveValue.timelineCards);
      
      // Extract editValues from saved cards to restore to TimeLine component
      // Since we saved merged cards, we need to restore editValues for all cards
      // so that validation can work on cards with empty fields
      const restoredEditValues: { [key: string]: { status: string; remark: string; project: string } } = {};
      saveValue.timelineCards.forEach((card: any) => {
        // Restore editValues for all cards so validation can work
        restoredEditValues[card.id] = {
          project: card.project || '',
          status: card.status || '',
          remark: card.remark || '',
        };
      });
      
      // Restore editValues to TimeLine component
      if (Object.keys(restoredEditValues).length > 0 && timelineRef.current?.setEditValues) {
        timelineRef.current.setEditValues(restoredEditValues);
      }
    }

    // Restore validation errors if they exist
    if (saveValue.validationErrors && typeof saveValue.validationErrors === 'object') {
      setCalendarValidationErrors(saveValue.validationErrors);
      // Set validation errors back to TimeLine component
      if (timelineRef.current?.setValidationErrors) {
        timelineRef.current.setValidationErrors(saveValue.validationErrors);
      }
    }

    // Set manual update flag to prevent polling from overwriting restored data
    isManualUpdateRef.current = true;

    // Call onConfirm callback after state updates
    if (onConfirm) {
      setTimeout(() => {
        onConfirm();
      }, 100);
    }
  }, [getCalendarSaveParamsValue, calendarPageKey, setTimelineCards, setPendingDeletes, timelineRef]);

  // Clear auto-save after successful save
  const clearCalendarAutoSave = useCallback(() => {
    deleteCalendarSaveParamsByPageKey(calendarPageKey);
    setPendingDeletes([]); // Clear pending deletes on successful save
  }, [deleteCalendarSaveParamsByPageKey, calendarPageKey, setPendingDeletes]);

  // Expose manual update ref for external operations
  const setManualUpdate = useCallback(() => {
    isManualUpdateRef.current = true;
  }, []);

  // Check if there are unsaved changes for RoutePrompt
  const hasCalendarUnsavedChanges = useMemo(() => {
    if (!isEditing) return false;
    
    const initialData = calendarInitialParamsRef.current;
    if (!initialData || initialData.timelineCards.length === 0) return false;
    
    const currentData = calendarCurrentSaveParams;
    
    // Compare using JSON stringify
    const hasChanges = JSON.stringify(initialData) !== JSON.stringify(currentData);
    
    return hasChanges;
  }, [isEditing, calendarCurrentSaveParams]);

  return {
    // State
    calendarAutoSaveVisible,
    calendarEditData,
    calendarPageKey,
    hasCalendarUnsavedChanges,

    // Handlers
    handleCalendarEdit,
    handleCalendarAutoSaveCancel,
    handleCalendarAutoSaveConfirm,
    clearCalendarAutoSave,
    setManualUpdate,
  };
};
